import { afterNextRender, Component, DestroyRef, inject, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslatePipe } from '@ngx-translate/core'
import { filter, map, of, switchMap, take, zip } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { AsyncPipe, Location } from '@angular/common'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { MeetingListComponent } from '../meeting-list/meeting-list.component'
import { Meeting, MeetingListResponse, MeetingsService, PastMeetingsType } from '../meetings.service'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { FormsModule } from '@angular/forms'
import { InfiniteData } from '@tanstack/query-core'
import { SpinnerComponent } from '../spinner/spinner.component'
import { UsersService } from '../users.service'
import { CalendarSyncService, CalendarSyncState, deriveCalendarSyncStateFromResult } from '../calendar-sync.service'
import { CalendarSyncStateComponent } from '../calendar-sync-state/calendar-sync-state.component'

@Component({
  selector: 'app-meetings-dashboard',
  standalone: true,
  imports: [TranslatePipe, AsyncPipe, ToolbarComponent, MeetingListComponent, MatButtonToggleModule, FormsModule, SpinnerComponent, CalendarSyncStateComponent],
  templateUrl: './meetings-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meetings-dashboard.component.scss',
})
export class MeetingsDashboardComponent implements OnInit {
  @ViewChild('pastMeetingList')
  pastMeetingList!: MeetingListComponent

  @ViewChild('upcomingMeetingList')
  upcomingMeetingList!: MeetingListComponent

  router = inject(Router)
  route = inject(ActivatedRoute)
  meetingsService = inject(MeetingsService)
  usersService = inject(UsersService)
  calendarSyncService = inject(CalendarSyncService)
  location = inject(Location)
  injector = inject(Injector)
  destroyRef = inject(DestroyRef)
  currentUser$ = this.usersService.currentUser$
  upcomingMeetingsQuery = this.meetingsService.getUpcomingMeetings()
  // Users whose first synchronization is long done never need the status endpoint, so the poll is
  // skipped for everyone but the small number of users still waiting on it.
  calendarSyncState$ = this.currentUser$.pipe(
    switchMap((user) =>
      user === null || user.calendarSyncCompletedAt !== null
        ? of<CalendarSyncState>('IDLE')
        : this.calendarSyncService.getCalendarSyncStatus().pipe(map((result) => deriveCalendarSyncStateFromResult(result)))
    )
  )
  pastMeetingsType$ = this.route.data.pipe(map((data) => (data['mode'] as PastMeetingsType) ?? 'OWN_MEETINGS'))
  pastMeetings$ = this.pastMeetingsType$.pipe(map((type) => this.meetingsService.getPastMeetings(type, false)))

  ngOnInit() {
    this.scrollToRightMeeting()
  }

  private scrollToRightMeeting() {
    zip(
      this.upcomingMeetingsQuery.result$.pipe(
        filter((result) => result.isSuccess),
        take(1)
      ),
      this.pastMeetings$.pipe(
        switchMap((query) => query.result$),
        filter((result) => result.isSuccess),
        take(1)
      )
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        afterNextRender(
          {
            write: () => {
              const pastMeetingId = this.route.snapshot.queryParamMap.get('pastMeetingId')
              if (pastMeetingId && this.pastMeetingList) {
                this.pastMeetingList.scrollToMeeting(+pastMeetingId)
              }
              const upcomingMeetingId = this.route.snapshot.queryParamMap.get('upcomingMeetingId')
              if (upcomingMeetingId && this.upcomingMeetingList) {
                this.upcomingMeetingList.scrollToMeeting(+upcomingMeetingId)
              }
            },
          },
          { injector: this.injector }
        )
      })
  }

  getAllMeetings(data: InfiniteData<MeetingListResponse, unknown>): Meeting[] {
    return data.pages.map((page) => page.meetings).flat()
  }

  prefetchMeeting(meeting: Meeting) {
    this.meetingsService.prefetchMeeting(meeting.id)
  }

  setPastMeetingQueryParameter(meetingId: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pastMeetingId: meetingId },
      queryParamsHandling: 'merge',
    })
  }

  setUpcomingMeetingQueryParameter(meetingId: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { upcomingMeetingId: meetingId },
      queryParamsHandling: 'merge',
    })
  }
}
